/**
 * Shared click-to-sort header helper for every list page.
 *
 * Drop-in usage inside a `<th>`:
 *
 *   <th onClick={() => onSort('jobId')}>
 *     <SortableHeader label="Job ID" sortKey="jobId" sort={sort} />
 *   </th>
 *
 * The `useSort` hook tracks the active column + direction, exposes a
 * stable `apply(rows, getValue)` that returns a sorted copy without
 * mutating the input.  Three click states: asc → desc → none.
 *
 * Values are sorted with a smart comparator:
 *   • Numbers (incl. numeric strings) — numeric compare
 *   • Dates (ISO yyyy-mm-dd or mm/dd/yyyy) — chronological
 *   • Everything else — case-insensitive locale string compare
 *   • Null / undefined always sorts to the bottom in either direction
 */
import { useCallback, useMemo, useState } from 'react';

export type SortDir = 'asc' | 'desc' | null;
export interface SortState {
  key: string | null;
  dir: SortDir;
}

export interface UseSortApi<T> {
  sort:    SortState;
  setSort: (key: string) => void;
  /** Set an EXPLICIT sort column + direction, or clear the sort (dir === null or
   *  key === null).  Unlike `setSort` — which cycles asc → desc → none on repeat
   *  clicks — this jumps straight to the requested state.  Used by the shared
   *  AppDataGrid header right-click menu's Sort Ascending / Sort Descending /
   *  Clear Sort items (a menu item must land on ONE known state, not a cycle). */
  setSortExplicit: (key: string | null, dir: SortDir) => void;
  /** Returns a new sorted array (or the original when no sort is active). */
  apply:   (rows: readonly T[], getValue: (row: T, key: string) => unknown) => T[];
  /** Pass-through helper to sort by the cell's formatted display string —
   *  handy when the row's raw value differs from what the user sees. */
  applyByFormatted: (rows: readonly T[], format: (row: T, key: string) => string) => T[];
}

/**
 * Click cycle: unsorted → asc → desc → unsorted.  Pass the same column key
 * to advance; pass a different key to switch column and start at asc.
 */
export function useSort<T>(initialKey: string | null = null,
                            initialDir: SortDir = null): UseSortApi<T> {
  const [sort, setSortState] = useState<SortState>({ key: initialKey, dir: initialDir });

  const setSort = useCallback((key: string) => {
    setSortState(prev => {
      if (prev.key !== key) return { key, dir: 'asc' };
      if (prev.dir === 'asc')  return { key, dir: 'desc' };
      if (prev.dir === 'desc') return { key: null, dir: null };
      return { key, dir: 'asc' };
    });
  }, []);

  const setSortExplicit = useCallback((key: string | null, dir: SortDir) => {
    setSortState(key === null || dir === null ? { key: null, dir: null } : { key, dir });
  }, []);

  const apply = useCallback(
    (rows: readonly T[], getValue: (row: T, key: string) => unknown): T[] => {
      if (!sort.key || !sort.dir) return rows as T[];
      const mult = sort.dir === 'asc' ? 1 : -1;
      const k    = sort.key;
      const copy = rows.slice();
      copy.sort((a, b) => mult * compareValues(getValue(a, k), getValue(b, k)));
      return copy;
    },
    [sort],
  );

  const applyByFormatted = useCallback(
    (rows: readonly T[], format: (row: T, key: string) => string): T[] => {
      if (!sort.key || !sort.dir) return rows as T[];
      const mult = sort.dir === 'asc' ? 1 : -1;
      const k    = sort.key;
      const copy = rows.slice();
      copy.sort((a, b) => mult * compareValues(format(a, k), format(b, k)));
      return copy;
    },
    [sort],
  );

  return useMemo(() => ({ sort, setSort, setSortExplicit, apply, applyByFormatted }),
                 [sort, setSort, setSortExplicit, apply, applyByFormatted]);
}

/** Smart comparator: nulls last, then number → date → locale string.
 *  Exported so the shared column-filter dropdown can order its value list with
 *  the EXACT same logic as the grid sort (CLAUDE.md §18.6 — one shared rule). */
export function compareValues(a: unknown, b: unknown): number {
  // Nulls last regardless of direction (callers can flip outside).
  const aNull = a === null || a === undefined || a === '';
  const bNull = b === null || b === undefined || b === '';
  if (aNull && bNull) return 0;
  if (aNull) return  1;
  if (bNull) return -1;

  // Number-like?  ("123", "1,234", 42 — all sort numerically.)
  const aNum = toNumber(a);
  const bNum = toNumber(b);
  if (aNum !== null && bNum !== null) return aNum - bNum;

  // Date-like? (ISO 2026-05-21 or US 05/21/2026)
  const aDate = toDate(a);
  const bDate = toDate(b);
  if (aDate !== null && bDate !== null) return aDate - bDate;

  // Fall back to locale string compare.
  return String(a).localeCompare(String(b), undefined, { numeric: true, sensitivity: 'base' });
}

function toNumber(v: unknown): number | null {
  if (typeof v === 'number') return Number.isFinite(v) ? v : null;
  if (typeof v !== 'string') return null;
  const cleaned = v.replace(/,/g, '').trim();
  if (cleaned === '') return null;
  const n = Number(cleaned);
  return Number.isFinite(n) ? n : null;
}

function toDate(v: unknown): number | null {
  if (typeof v !== 'string') return null;
  // ISO yyyy-mm-dd[Thh:mm…] or US mm/dd/yyyy — bail out early on anything
  // that doesn't look date-ish so plain numbers/strings don't false-positive.
  if (!/^\d{1,4}[-/]\d{1,2}[-/]\d{1,4}/.test(v)) return null;
  const t = Date.parse(v);
  return Number.isNaN(t) ? null : t;
}

// ─────────────────────────────────────────────────────────────────────────────
// <SortableHeader> — visual indicator for the active sort
// ─────────────────────────────────────────────────────────────────────────────
interface HeaderProps {
  label:    string;
  sortKey:  string;
  sort:     SortState;
  /** Optional extra content rendered after the label, e.g. column filter button. */
  children?: React.ReactNode;
}

/**
 * Renders the column label + a sort indicator that reflects current state.
 * Wrap the whole `<th>` with `onClick={() => api.setSort(key)}` so clicking
 * anywhere on the header advances the sort.
 */
export function SortableHeader({ label, sortKey, sort, children }: HeaderProps) {
  const active = sort.key === sortKey && sort.dir !== null;
  const arrow  = !active ? '⇅' : sort.dir === 'asc' ? '▲' : '▼';
  return (
    <span className="col-hdr-cell" style={{ cursor: 'pointer', userSelect: 'none' }}>
      <span className="col-hdr-label">{label}</span>
      <span
        aria-label={active ? `sorted ${sort.dir}ending` : 'sort'}
        style={{
          // UI-3: sort arrow at the column's right end, light grey, never bold.
          marginLeft: 'auto',
          paddingLeft: 6,
          fontSize: 12,
          color: active ? '#9aa3ad' : '#bbb',
          fontWeight: 400,
          minWidth: 12,
          display: 'inline-block',
          lineHeight: 1,
          opacity: 1,
        }}
      >{arrow}</span>
      {children}
    </span>
  );
}
